import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  Activity,
  Calendar,
  Clock,
  Users,
  ChevronLeft,
  Target,
  Zap,
  Gauge,
  Timer,
  User,
  MapPin,
  AlertCircle,
  CheckCircle,
  XCircle,
  CircleDot
} from 'lucide-react';
import api from '@/services/api';
import { format } from 'date-fns';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter
} from 'recharts';

interface SessionDetail {
  id: string;
  title: string;
  date: string;
  type: 'Practice' | 'Match' | 'Training' | 'Assessment';
  status: 'Scheduled' | 'InProgress' | 'Completed' | 'Cancelled';
  coach: {
    id: string;
    name: string;
    email: string;
  };
  players: Array<{
    id: string;
    name: string;
    email: string;
    battingHand?: string;
    experienceLevel?: string;
  }>;
  duration?: number;
  notes?: string;
  shotData: Array<{
    id: string;
    timestamp: string;
    shotType: string;
    quality: 'EXCELLENT' | 'GOOD' | 'AVERAGE' | 'POOR';
    ballSpeed: number;
    batSpeed: number;
    exitVelocity: number;
    launchAngle: number;
    horizontalAngle: number;
    distance: number;
    height: number;
    spinRate?: number;
    impactLocationX: number;
    impactLocationY: number;
    sweetSpotDistance: number;
    powerTransfer: number;
    timing?: string;
    footwork?: string;
    balance?: string;
    followThrough?: string;
    player: {
      id: string;
      name: string;
    };
  }>;
  shotAnalyses?: Array<any>;
  metadata?: any;
}

interface SessionStatistics {
  totalShots: number;
  byPlayer: Record<string, any>;
  byShotType: Record<string, number>;
  qualityDistribution: {
    EXCELLENT: number;
    GOOD: number;
    AVERAGE: number;
    POOR: number;
  };
  averages: {
    exitVelocity: number;
    distance: number;
    powerTransfer: number;
  };
}

const QUALITY_COLORS = {
  EXCELLENT: '#10b981',
  GOOD: '#3b82f6',
  AVERAGE: '#f59e0b',
  POOR: '#ef4444'
};

const SHOT_TYPE_COLORS = [
  '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6'
];

export function SessionDetailPage() {
  const { id } = useParams<{ id: string }>();
  useAuth(); // Authentication check
  const navigate = useNavigate();
  const [session, setSession] = useState<SessionDetail | null>(null);
  const [statistics, setStatistics] = useState<SessionStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (id) {
      fetchSessionDetail();
    }
  }, [id]);

  const fetchSessionDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/sessions/${id}`);
      setSession(response.data.session);
      setStatistics(response.data.statistics);
    } catch (error) {
      console.error('Failed to fetch session details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading session details...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-semibold mb-2">Session not found</p>
            <p className="text-muted-foreground mb-4">The session you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/sessions')}>
              Back to Sessions
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'InProgress':
        return 'bg-green-500';
      case 'Completed':
        return 'bg-blue-500';
      case 'Scheduled':
        return 'bg-yellow-500';
      case 'Cancelled':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getQualityIcon = (quality: string) => {
    switch (quality) {
      case 'EXCELLENT':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'GOOD':
        return <CircleDot className="h-4 w-4 text-blue-500" />;
      case 'AVERAGE':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'POOR':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  // Prepare chart data
  const qualityChartData = statistics ? Object.entries(statistics.qualityDistribution).map(([key, value]) => ({
    name: key,
    value,
    fill: QUALITY_COLORS[key as keyof typeof QUALITY_COLORS]
  })) : [];

  const shotTypeChartData = statistics ? Object.entries(statistics.byShotType).map(([key, value], index) => ({
    name: key,
    shots: value,
    fill: SHOT_TYPE_COLORS[index % SHOT_TYPE_COLORS.length]
  })) : [];

  const playerPerformanceData = statistics ? Object.values(statistics.byPlayer).map((player: any) => ({
    name: player.playerName,
    shots: player.totalShots,
    avgVelocity: Math.round(player.averageExitVelocity),
    avgDistance: Math.round(player.averageDistance),
    maxDistance: Math.round(player.maxDistance)
  })) : [];

  const shotProgressData = session.shotData.map((shot, index) => ({
    shot: index + 1,
    velocity: shot.exitVelocity,
    distance: shot.distance,
    quality: shot.quality
  }));

  const scatterData = session.shotData.map(shot => ({
    x: shot.exitVelocity,
    y: shot.launchAngle,
    z: shot.distance,
    quality: shot.quality
  }));

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/sessions')}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">{session.title}</h1>
                <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(session.date), 'PPP')}
                  </span>
                  {session.duration && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {session.duration} minutes
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {session.players.length} players
                  </span>
                </div>
              </div>
            </div>
            <Badge className={getStatusColor(session.status)}>
              {session.status}
            </Badge>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="shots">Shot Data</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="players">Players</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Key Metrics */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Shots</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{statistics?.totalShots || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    Across {session.players.length} players
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Exit Velocity</CardTitle>
                  <Zap className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {statistics?.averages.exitVelocity.toFixed(1)} m/s
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Ball speed after impact
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Distance</CardTitle>
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {statistics?.averages.distance.toFixed(1)}m
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Shot carry distance
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Power Transfer</CardTitle>
                  <Gauge className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {((statistics?.averages?.powerTransfer ?? 0) * 100).toFixed(1)}%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Energy efficiency
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid gap-4 md:grid-cols-2">
              {/* Quality Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Shot Quality Distribution</CardTitle>
                  <CardDescription>Breakdown of shot quality ratings</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={qualityChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {qualityChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Shot Types */}
              <Card>
                <CardHeader>
                  <CardTitle>Shot Types</CardTitle>
                  <CardDescription>Distribution of different shot types</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={shotTypeChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="shots" fill="#8884d8">
                        {shotTypeChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Session Info */}
            <Card>
              <CardHeader>
                <CardTitle>Session Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Coach</p>
                    <p className="font-medium">{session.coach.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Session Type</p>
                    <p className="font-medium">{session.type}</p>
                  </div>
                </div>
                {session.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm">{session.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Shot Data Tab */}
          <TabsContent value="shots" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Shot-by-Shot Analysis</CardTitle>
                <CardDescription>Detailed breakdown of each shot in the session</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {session.shotData.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No shots recorded yet</p>
                  ) : (
                    session.shotData.map((shot, index) => (
                      <div key={shot.id} className="border rounded-lg p-4 hover:bg-accent/50 transition-colors">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-sm font-semibold">#{index + 1}</span>
                            </div>
                            <div>
                              <p className="font-medium">{shot.shotType}</p>
                              <p className="text-sm text-muted-foreground">
                                {shot.player.name} • {format(new Date(shot.timestamp), 'HH:mm:ss')}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {getQualityIcon(shot.quality)}
                            <Badge variant={shot.quality === 'EXCELLENT' ? 'default' : 'secondary'}>
                              {shot.quality}
                            </Badge>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Exit Velocity</p>
                            <p className="font-medium">{shot.exitVelocity.toFixed(1)} m/s</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Distance</p>
                            <p className="font-medium">{shot.distance.toFixed(1)}m</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Launch Angle</p>
                            <p className="font-medium">{shot.launchAngle.toFixed(1)}°</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Power Transfer</p>
                            <p className="font-medium">{(shot.powerTransfer * 100).toFixed(1)}%</p>
                          </div>
                        </div>

                        {(shot.timing || shot.footwork || shot.balance) && (
                          <div className="mt-3 pt-3 border-t">
                            <div className="flex gap-4 text-sm">
                              {shot.timing && (
                                <div className="flex items-center gap-1">
                                  <Timer className="h-3 w-3" />
                                  <span className="text-muted-foreground">Timing:</span>
                                  <span>{shot.timing}</span>
                                </div>
                              )}
                              {shot.footwork && (
                                <div className="flex items-center gap-1">
                                  <Activity className="h-3 w-3" />
                                  <span className="text-muted-foreground">Footwork:</span>
                                  <span>{shot.footwork}</span>
                                </div>
                              )}
                              {shot.balance && (
                                <div className="flex items-center gap-1">
                                  <Target className="h-3 w-3" />
                                  <span className="text-muted-foreground">Balance:</span>
                                  <span>{shot.balance}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {/* Shot Progress Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Session Progress</CardTitle>
                <CardDescription>Shot velocity and distance over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={shotProgressData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="shot" label={{ value: 'Shot Number', position: 'insideBottom', offset: -5 }} />
                    <YAxis yAxisId="left" label={{ value: 'Velocity (m/s)', angle: -90, position: 'insideLeft' }} />
                    <YAxis yAxisId="right" orientation="right" label={{ value: 'Distance (m)', angle: 90, position: 'insideRight' }} />
                    <Tooltip />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="velocity" stroke="#8884d8" name="Exit Velocity" />
                    <Line yAxisId="right" type="monotone" dataKey="distance" stroke="#82ca9d" name="Distance" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Velocity vs Launch Angle Scatter */}
            <Card>
              <CardHeader>
                <CardTitle>Velocity vs Launch Angle</CardTitle>
                <CardDescription>Relationship between exit velocity and launch angle</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="x" name="Exit Velocity" unit=" m/s" />
                    <YAxis dataKey="y" name="Launch Angle" unit="°" />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                    <Scatter name="Shots" data={scatterData} fill="#8884d8">
                      {scatterData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={QUALITY_COLORS[entry.quality as keyof typeof QUALITY_COLORS]} />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Player Comparison */}
            <Card>
              <CardHeader>
                <CardTitle>Player Performance Comparison</CardTitle>
                <CardDescription>Key metrics by player</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={playerPerformanceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="shots" fill="#8884d8" name="Total Shots" />
                    <Bar dataKey="avgVelocity" fill="#82ca9d" name="Avg Velocity (m/s)" />
                    <Bar dataKey="avgDistance" fill="#ffc658" name="Avg Distance (m)" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Players Tab */}
          <TabsContent value="players" className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {session.players.map((player) => {
                const playerStats = statistics?.byPlayer[player.id];
                return (
                  <Card key={player.id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-base">{player.name}</CardTitle>
                            <CardDescription>{player.email}</CardDescription>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {player.battingHand && (
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Batting Hand</span>
                            <span className="font-medium">{player.battingHand}</span>
                          </div>
                        )}
                        {player.experienceLevel && (
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Experience</span>
                            <span className="font-medium">{player.experienceLevel}</span>
                          </div>
                        )}
                        {playerStats && (
                          <>
                            <Separator />
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Total Shots</span>
                                <span className="font-medium">{playerStats.totalShots}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Avg Velocity</span>
                                <span className="font-medium">{playerStats.averageExitVelocity.toFixed(1)} m/s</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Avg Distance</span>
                                <span className="font-medium">{playerStats.averageDistance.toFixed(1)}m</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Max Distance</span>
                                <span className="font-medium">{playerStats.maxDistance.toFixed(1)}m</span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}